import { isApiOk } from '@/services/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type {
  Role,
  CreateRoleDto,
  UpdateRoleDto,
  Permission,
  PermissionTemplate,
  AssignedUser,
  UnassignedUser,
  ImportResult,
} from '@/types/role'
import type { ApiResponse } from '@/services/types'
import { ApiError } from '@/utils/apiError'
import {
  getRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  getAssignedUsers,
  getAvailableUsers,
  assignUsersToRole,
  removeUsersFromRole,
  getPermissionTree,
  getRolePermissions,
  setRolePermissions,
  cloneRole,
  saveAsTemplate,
  getTemplates,
  deleteTemplate,
  createRoleFromTemplate,
  exportRolesToExcel,
  downloadImportTemplate,
  importRolesFromExcel,
} from '@/services/roleService'

/**
 * 【修复角色权限三审#14】错误口径统一：
 * - 读列表/详情：失败置 error，供页面提示（不再静默吞错）；
 * - 透传型读写：只回报成败，错误由调用方展示（仿任务域 store 收口）；
 * - fetchAssignedUsers/fetchAvailableUsers/fetchTemplates 返回完整 ApiResponse，
 *   不再把失败降级成 []（空列表与加载失败是两种语义，此前页面无法区分）。
 */

export const useRoleStore = defineStore('role', () => {
  // ======== State ========
  const roles = ref<Role[]>([])
  const currentRole = ref<Role | null>(null)
  const permissionTree = ref<Permission[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const searchKeyword = ref('')
  const typeFilter = ref<'all' | 'builtin' | 'custom'>('all')

  // ======== Role CRUD ========
  async function fetchRoles(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const res = await getRoles({ keyword: searchKeyword.value, type: typeFilter.value })
      if (res.data) roles.value = res.data
      else error.value = res.message ?? '加载角色列表失败'
    } catch (e) {
      error.value = e instanceof Error ? e.message : '未知错误'
    } finally {
      loading.value = false
    }
  }

  async function fetchRoleById(id: string): Promise<Role | null> {
    loading.value = true
    error.value = null
    try {
      const res = await getRoleById(id)
      if (res.data) {
        currentRole.value = res.data
        return res.data
      }
      error.value = res.message ?? '获取角色详情失败'
      return null
    } catch (e) {
      error.value = e instanceof Error ? e.message : '未知错误'
      return null
    } finally {
      loading.value = false
    }
  }

  async function addRole(data: CreateRoleDto): Promise<ApiResponse<Role>> {
    loading.value = true
    error.value = null
    try {
      const res = await createRole(data)
      if (res.data) {
        roles.value.unshift(res.data)
      } else {
        error.value = res.message ?? '创建角色失败'
      }
      return res
    } catch (e) {
      const msg = e instanceof Error ? e.message : '未知错误'
      error.value = msg
      return { code: 500, data: undefined, message: msg}
    } finally {
      loading.value = false
    }
  }

  async function editRole(id: string, data: UpdateRoleDto): Promise<ApiResponse<Role>> {
    loading.value = true
    error.value = null
    try {
      const res = await updateRole(id, data)
      if (res.data) {
        const idx = roles.value.findIndex((r) => r.id === id)
        if (idx !== -1) roles.value[idx] = res.data
        if (currentRole.value?.id === id) currentRole.value = res.data
      } else {
        // 【修复角色权限三审#14】乐观锁冲突识别不再只靠裸文案匹配：
        // BusinessException(String) 默认业务码 400 走此分支时只能按文案判；
        // ResultCode.CONFLICT（版本校验路径）走 catch 的 ApiError.code===409
        error.value = res.message?.includes('已被他人修改')
          ? '数据已被他人修改，请刷新后重试'
          : res.message ?? '编辑角色失败'
      }
      return res
    } catch (e) {
      const msg = e instanceof ApiError && e.code === 409
        ? '数据已被他人修改，请刷新后重试'
        : e instanceof Error
          ? e.message
          : '未知错误'
      error.value = msg
      return { code: 500, data: undefined, message: msg}
    } finally {
      loading.value = false
    }
  }

  async function removeRole(id: string, transferToRoleId?: string): Promise<ApiResponse<void>> {
    loading.value = true
    error.value = null
    try {
      const res = await deleteRole(id, transferToRoleId)
      if (isApiOk(res)) {
        roles.value = roles.value.filter((r) => r.id !== id)
        if (currentRole.value?.id === id) currentRole.value = null
      } else {
        error.value = res.message ?? '删除角色失败'
      }
      return res
    } catch (e) {
      const msg = e instanceof Error ? e.message : '未知错误'
      error.value = msg
      return { code: 500, data: undefined, message: msg}
    } finally {
      loading.value = false
    }
  }

  // ======== Permission ========
  async function fetchPermissionTree(): Promise<void> {
    try {
      const res = await getPermissionTree()
      if (res.data) permissionTree.value = res.data
      else error.value = res.message ?? '加载权限目录失败'
    } catch (e) {
      // 【修复角色权限三审#14】原实现空 catch 静默吞错，权限页会停在空树且无从提示
      error.value = e instanceof Error ? e.message : '未知错误'
    }
  }

  async function getRolePermIds(roleId: string): Promise<ApiResponse<string[]>> {
    return await getRolePermissions(roleId)
  }

  async function saveRolePermissions(
    roleId: string,
    permissionIds: string[]
  ): Promise<ApiResponse<void>> {
    return await setRolePermissions(roleId, permissionIds)
  }

  // ======== User Assignment ========
  async function fetchAssignedUsers(
    roleId: string
  ): Promise<ApiResponse<AssignedUser[]>> {
    return await getAssignedUsers(roleId)
  }

  async function fetchAvailableUsers(
    roleId: string,
    keyword?: string
  ): Promise<ApiResponse<UnassignedUser[]>> {
    return await getAvailableUsers(roleId, keyword)
  }

  async function addUsersToRole(roleId: string, userIds: string[]): Promise<ApiResponse<void>> {
    return await assignUsersToRole(roleId, userIds)
  }

  async function removeUsersFromRoleAction(
    roleId: string,
    userIds: string[]
  ): Promise<ApiResponse<void>> {
    return await removeUsersFromRole(roleId, userIds)
  }

  // ======== Clone & Template ========
  async function duplicateRole(
    sourceRoleId: string,
    data: { name: string; code: string; scope: 'admin' | 'workspace'; description?: string }
  ): Promise<ApiResponse<Role>> {
    return await cloneRole(sourceRoleId, data)
  }

  async function createTemplate(
    sourceRoleId: string,
    templateName: string
  ): Promise<ApiResponse<PermissionTemplate>> {
    return await saveAsTemplate(sourceRoleId, templateName)
  }

  async function fetchTemplates(): Promise<ApiResponse<PermissionTemplate[]>> {
    return await getTemplates()
  }

  async function removeTemplate(id: string): Promise<ApiResponse<void>> {
    return await deleteTemplate(id)
  }

  async function createFromTemplate(
    templateId: string,
    data: { name: string; code: string; scope: 'admin' | 'workspace'; description?: string }
  ): Promise<ApiResponse<Role>> {
    return await createRoleFromTemplate(templateId, data)
  }

  // ======== Import/Export ========
  async function exportRoles(): Promise<ApiResponse<Blob>> {
    return await exportRolesToExcel()
  }

  async function downloadTemplate(): Promise<ApiResponse<Blob>> {
    return await downloadImportTemplate()
  }

  async function importRoles(file: File): Promise<ApiResponse<ImportResult>> {
    return await importRolesFromExcel(file)
  }

  return {
    // State
    roles, currentRole, permissionTree, loading, error, searchKeyword, typeFilter,
    // Role CRUD
    fetchRoles, fetchRoleById, addRole, editRole, removeRole,
    // Permission
    fetchPermissionTree, getRolePermIds, saveRolePermissions,
    // User Assignment
    fetchAssignedUsers, fetchAvailableUsers, addUsersToRole, removeUsersFromRole: removeUsersFromRoleAction,
    // Clone & Template
    duplicateRole, createTemplate, fetchTemplates, removeTemplate, createFromTemplate,
    // Import/Export
    exportRoles, downloadTemplate, importRoles,
  }
})
